import { put, call, select, take, takeEvery } from 'redux-saga/effects';
import camelCase from 'camel-case';
import { is, path, propOr, pathOr } from 'ramda';
import { notificationsAdd } from 'virtues-bootstrap';
import { show as showModal } from 'redux-modal';

import * as api from 'services/api/request';
import i18n from 'services/i18n';

import { authUnauthorized } from 'store/actionCreators/auth';

import { TOKEN_EXPIRED } from 'constants/modals';

import { getUniqId } from 'utilities';
import { sagaPatternOnModalClose } from 'utilities/modal';
import { getIsAuthLoading } from 'store/selectors/auth';

export function* handleRequest(action) {
  const meta = { primaryPayload: action.payload };

  try {
    const methodName = camelCase(action.type);
    const method = api[methodName];

    if (!method) {
      throw new Error(`Method "${methodName}" not found in src/services/api/request`);
    }

    const response = yield call(method, action.payload);

    yield put({
      type: `${action.type}_SUCCESS`,
      payload: {
        response: response.data,
        statusCode: response.status,
      },
      meta,
    });
  } catch (error) {
    if (error.response) {
      if (error.response.status === 401) {
        const loading = yield select(getIsAuthLoading);
        if (!loading) {
          yield put(showModal(TOKEN_EXPIRED));
          yield take(sagaPatternOnModalClose(TOKEN_EXPIRED));
        }

        yield put(authUnauthorized());
        return;
      }

      yield put({
        type: `${action.type}_FAILURE`,
        payload: {
          response: error.response.data,
          statusCode: error.response.status,
        },
        meta,
      });

      const errorData = path(['response', 'data'])(error);
      const smileError = pathOr(path(['message', 'error_description'])(errorData), [
        'message',
        'messages',
        '0',
        'message',
      ])(errorData);
      const defaultError = propOr(path(['issue', '0', 'diagnostics'])(errorData), 'message')(
        errorData,
      );
      const largeFileError = error.response.status === 413 && 'File is too large';

      const message = smileError || defaultError || largeFileError;
      yield put(
        notificationsAdd({
          id: getUniqId(),
          variant: 'danger',
          title: i18n.t('common:requestError'),
          description: is(Object, message) ? JSON.stringify(message) : message,
        }),
      );
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      yield put({
        type: `${action.type}_FAILURE`,
        payload: { response: null, statusCode: null },
        meta,
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error', error.message); // eslint-disable-line no-console
    }
  }
}

const requestIntentAction = ({ type }) =>
  type.startsWith('REQUEST/') && !type.endsWith('_SUCCESS') && !type.endsWith('_FAILURE');

export default function* root() {
  yield takeEvery(requestIntentAction, handleRequest);
}
